import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Sidebar from '../Components/Sidebar/Sidebar';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import { fetchSprints, fetchSprint, fetchChamadosSprint, fetchBurndownSprint, createSprint } from '../../api/sprints';
import SprintGantt from './SprintGantt';
import { fetchAnalistas } from '../../api/usuarios';
import { getAnalistaDisplayNome } from '../../FuncoesGerais/tipoAnalista';
import './Sprint.css';

const FILTROS = [
    { key: 'todas', label: 'Todas' },
    { key: 'nao_iniciadas', label: 'Não iniciadas' },
    { key: 'em_andamento', label: 'Em andamento' },
    { key: 'fechadas', label: 'Fechadas' }
];

function formatarData(d) {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function buildClientBurndown(detalhe, chamados) {
    if (!detalhe || !detalhe.data_inicio || !detalhe.data_fim) return [];
    const parse = (v) => {
        const s = String(v).slice(0, 10);
        const [y, m, d] = s.split('-').map(Number);
        if (!y || !m || !d) return null;
        const dt = new Date(y, m - 1, d);
        dt.setHours(0, 0, 0, 0);
        return dt;
    };
    const inicio = parse(detalhe.data_inicio);
    const fim = parse(detalhe.data_fim);
    if (!inicio || !fim) return [];
    const msDia = 24 * 60 * 60 * 1000;
    const totalDias = Math.max(1, Math.round((fim - inicio) / msDia) + 1);
    const lista = Array.isArray(chamados) ? chamados : [];
    const totalHoras = lista.reduce((acc, c) => acc + (Number(c.estimativa_horas) || 0), 0);
    const remaining = lista.reduce((acc, c) => {
        if ((c.status || '').toLowerCase() === 'concluido') return acc;
        const est = Number(c.estimativa_horas) || 0;
        const trab = Number(c.tempo_trabalhado_horas) || 0;
        return acc + Math.max(0, est - trab);
    }, 0);
    const gasto = lista.reduce((acc, c) => acc + (Number(c.tempo_trabalhado_horas) || 0), 0);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    let diaHoje = Math.round((hoje - inicio) / msDia);
    if (diaHoje < 0) diaHoje = 0;
    if (diaHoje > totalDias - 1) diaHoje = totalDias - 1;
    return Array.from({ length: totalDias }, (_, dia) => {
        const dt = new Date(inicio.getTime() + dia * msDia);
        const ideal = totalDias <= 1 ? 0 : Math.round(totalHoras * (1 - dia / (totalDias - 1)) * 10) / 10;
        const isHoje = dia === diaHoje;
        return {
            dia: dia + 1,
            label: dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
            ideal,
            real: isHoje ? Math.round(remaining * 10) / 10 : null,
            trabalhado: isHoje ? Math.round(gasto * 10) / 10 : null
        };
    });
}

function resolverBurndown(pontos, detalhe, chamados) {
    if (Array.isArray(pontos) && pontos.length > 0) return pontos;
    return buildClientBurndown(detalhe, chamados);
}

function SprintHome() {
    const history = useHistory();
    const [sprints, setSprints] = useState([]);
    const [sprintsDetalhe, setSprintsDetalhe] = useState({});
    const [loading, setLoading] = useState(true);
    const [, setLoadingDetalhes] = useState(false);
    const [filtro, setFiltro] = useState('em_andamento');
    const [visao, setVisao] = useState('cards');
    const [mensagem, setMensagem] = useState('');
    const [showModalNova, setShowModalNova] = useState(false);
    const [novaNome, setNovaNome] = useState('');
    const [novaDataInicio, setNovaDataInicio] = useState('');
    const [novaDataFim, setNovaDataFim] = useState('');
    const [novaResponsavelId, setNovaResponsavelId] = useState('');
    const [analistas, setAnalistas] = useState([]);
    const [chamadosBySprintId, setChamadosBySprintId] = useState({});
    const [burndownBySprintId, setBurndownBySprintId] = useState({});

    const verificar = useCallback(async () => {
        const resultado = await verificandoApp('ATENDENTE');
        if (!resultado) history.push('/');
    }, [history]);

    const loadSprints = useCallback(async () => {
        verificandoToken();
        setLoading(true);
        try {
            const list = await fetchSprints();
            const arr = Array.isArray(list) ? list : [];
            setSprints(arr);
            setSprintsDetalhe({});
            setChamadosBySprintId({});
            setBurndownBySprintId({});
        } catch (e) {
            setSprints([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        verificar();
        loadSprints();
    }, [verificar, loadSprints]);

    useEffect(() => {
        if (sprints.length === 0) return;
        setLoadingDetalhes(true);
        Promise.all(sprints.map((s) => Promise.all([
            fetchSprint(s.id),
            fetchChamadosSprint(s.id),
            fetchBurndownSprint(s.id)
        ])))
            .then((results) => {
                const detailMap = {};
                const chamadosMap = {};
                const burndownMap = {};
                sprints.forEach((s, i) => {
                    const [det, list, pontos] = results[i] || [null, [], []];
                    const detalhe = det || s;
                    const lista = Array.isArray(list) ? list : [];
                    detailMap[s.id] = detalhe;
                    chamadosMap[s.id] = lista;
                    burndownMap[s.id] = resolverBurndown(pontos, detalhe, lista);
                });
                setSprintsDetalhe(detailMap);
                setChamadosBySprintId(chamadosMap);
                setBurndownBySprintId(burndownMap);
            })
            .finally(() => setLoadingDetalhes(false));
    }, [sprints]);

    const sprintsFiltradas = useMemo(() => {
        return sprints.filter((s) => {
            const det = sprintsDetalhe[s.id] || s;
            const ativo = s.ativo === 1 || s.ativo === true;
            const fechada = !!(det.concluida_em || s.concluida_em);
            if (filtro === 'todas') return true;
            if (filtro === 'nao_iniciadas') return !ativo && !fechada;
            if (filtro === 'em_andamento') return ativo;
            if (filtro === 'fechadas') return fechada;
            return true;
        });
    }, [sprints, sprintsDetalhe, filtro]);

    const handleCriarSprint = async () => {
        verificandoToken();
        const nome = (novaNome || '').trim();
        if (!nome) {
            setMensagem('Informe o nome da sprint.');
            return;
        }
        try {
            await createSprint({
                nome,
                data_inicio: novaDataInicio || null,
                data_fim: novaDataFim || null,
                responsavel_id: novaResponsavelId ? Number(novaResponsavelId) : null
            });
            setShowModalNova(false);
            setNovaNome('');
            setNovaDataInicio('');
            setNovaDataFim('');
            setNovaResponsavelId('');
            setMensagem('');
            await loadSprints();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao criar sprint.');
        }
    };

    const abrirModalNova = async () => {
        setMensagem('');
        setShowModalNova(true);
        try {
            const list = await fetchAnalistas();
            setAnalistas(Array.isArray(list) ? list : []);
            const codUsu = localStorage.getItem('codUsu');
            setNovaResponsavelId(codUsu && codUsu !== '0' ? String(codUsu) : '');
        } catch (_) {
            setAnalistas([]);
        }
    };

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="container-fluid titulo cadastro-unificado sprint-page">
                    <div className="page-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <h1 className="mb-0">Sprint de Chamados</h1>
                        <button type="button" className="btn btn-primary btn-sm" onClick={abrirModalNova}>
                            Nova sprint
                        </button>
                    </div>

                    {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}

                    {loading && <p className="text-muted">Carregando sprints...</p>}

                    {!loading && (
                        <>
                            <div className="sprint-home-filtro mb-3 d-flex flex-wrap align-items-center gap-2">
                                <span className="sprint-home-filtro-label">Filtrar:</span>
                                {FILTROS.map((f) => (
                                    <button
                                        key={f.key}
                                        type="button"
                                        className={`btn btn-sm ${filtro === f.key ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setFiltro(f.key)}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                                <span className="ms-auto sprint-home-visao">
                                    <button type="button" className={`btn btn-sm ${visao === 'cards' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setVisao('cards')} title="Cartões">
                                        <i className="fas fa-th-large" />
                                    </button>
                                    <button type="button" className={`btn btn-sm ${visao === 'gantt' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setVisao('gantt')} title="Linha do tempo">
                                        <i className="fas fa-stream" />
                                    </button>
                                </span>
                            </div>

                            {sprintsFiltradas.length === 0 ? (
                                <div className="alert alert-info py-3">
                                    {sprints.length === 0
                                        ? 'Nenhuma sprint cadastrada. Clique em Nova sprint para criar.'
                                        : `Nenhuma sprint no filtro "${FILTROS.find((x) => x.key === filtro)?.label}".`}
                                </div>
                            ) : visao === 'gantt' ? (
                                <SprintGantt
                                    sprints={sprintsFiltradas}
                                    detalhes={sprintsDetalhe}
                                    chamadosPorSprint={chamadosBySprintId}
                                    onAbrir={(id) => history.push(`/sprint/${id}`)}
                                />
                            ) : (
                                <div className="sprint-home-cards row g-3 mb-4">
                                    {sprintsFiltradas.map((s) => {
                                        const det = sprintsDetalhe[s.id] || s;
                                        const chamadosCard = chamadosBySprintId[s.id] || [];
                                        const burnDownDataCard = burndownBySprintId[s.id] || [];
                                        const totalHorasCard = chamadosCard.reduce((acc, c) => acc + (Number(c.estimativa_horas) || 0), 0);
                                        const ativo = s.ativo === 1 || s.ativo === true;
                                        const fechada = !!(det.concluida_em || s.concluida_em);
                                        return (
                                            <div key={s.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                                                <div
                                                    className="card sprint-home-card h-100"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => history.push(`/sprint/${s.id}`)}
                                                    onKeyDown={(e) => e.key === 'Enter' && history.push(`/sprint/${s.id}`)}
                                                >
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <h5 className="card-title mb-0 sprint-home-card-titulo">{det.nome || s.nome || 'Sprint'}</h5>
                                                            <span className={`badge ${ativo ? 'bg-success' : fechada ? 'bg-secondary' : 'bg-light text-dark'}`}>
                                                                {ativo ? 'Ativa' : fechada ? 'Fechada' : 'Não iniciada'}
                                                            </span>
                                                        </div>
                                                        <p className="card-text text-muted small mb-1">
                                                            <i className="fas fa-user-tie me-1" />
                                                            {det.responsavel_nome || '—'}
                                                        </p>
                                                        <p className="card-text text-muted small mb-2">
                                                            <i className="fas fa-calendar-alt me-1" />
                                                            {formatarData(det.data_inicio || s.data_inicio)} a {formatarData(det.data_fim || s.data_fim)}
                                                        </p>
                                                        <div className="sprint-home-card-metricas d-flex gap-2 flex-wrap mb-2">
                                                            <span className="sprint-home-metrica"><strong>Progresso:</strong> {det.percentual_conclusao ?? 0}%</span>
                                                            <span className="sprint-home-metrica"><strong>Itens:</strong> {det.concluidos ?? 0}/{det.total_chamados ?? 0}</span>
                                                        </div>
                                                        <div className="sprint-home-card-burndown mt-3 pt-3 border-top">
                                                            {burnDownDataCard.length === 0 ? (
                                                                <p className="text-muted small mb-0">Defina o período na sprint para ver o burn down.</p>
                                                            ) : (
                                                                <>
                                                                    {totalHorasCard <= 0 && (
                                                                        <p className="text-muted small mb-1">Informe estimativa nos itens para a linha Ideal.</p>
                                                                    )}
                                                                    <div className="sprint-home-burndown-chart-wrap">
                                                                        <div className="sprint-home-burndown-chart">
                                                                            <ResponsiveContainer width="100%" height={120}>
                                                                                <LineChart data={burnDownDataCard} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                                                                                    <CartesianGrid strokeDasharray="2 2" />
                                                                                    <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                                                                                    <YAxis tick={{ fontSize: 9 }} unit=" h" width={28} />
                                                                                    <Tooltip
                                                                                        formatter={(value, name) => {
                                                                                            const lbl = name === 'ideal' ? 'Planejado' : name === 'real' ? 'Real' : 'Trabalhado';
                                                                                            return [value != null ? `${value} h` : '—', lbl];
                                                                                        }}
                                                                                        contentStyle={{ fontSize: '11px' }}
                                                                                    />
                                                                                    <Line type="monotone" dataKey="ideal" stroke="var(--bs-primary)" strokeWidth={1.5} dot={false} name="ideal" />
                                                                                    <Line type="monotone" dataKey="real" stroke="var(--bs-success)" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} name="real" connectNulls={false} />
                                                                                    <Line type="monotone" dataKey="trabalhado" stroke="var(--bs-warning)" strokeWidth={1.5} dot={{ r: 2 }} name="trabalhado" connectNulls={false} />
                                                                                </LineChart>
                                                                            </ResponsiveContainer>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-muted small mb-0 mt-2 text-center">Burn down · acima da linha ideal = atraso</p>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>

            {showModalNova && (
                <div className="modal-overlay" onClick={() => setShowModalNova(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3">Nova sprint</h5>
                        <div className="form-group mb-2">
                            <label>Nome</label>
                            <input value={novaNome} onChange={(e) => setNovaNome(e.target.value)} className="form-control form-control-sm" placeholder="Ex.: Sprint 1" />
                        </div>
                        <div className="form-group mb-2">
                            <label>Responsável (gerente / desenvolvedor)</label>
                            <select value={novaResponsavelId} onChange={(e) => setNovaResponsavelId(e.target.value)} className="form-control form-control-sm">
                                <option value="">Selecione...</option>
                                {analistas.map((a) => (
                                    <option key={a.USUSEQ ?? a.ususeq} value={a.USUSEQ ?? a.ususeq}>{getAnalistaDisplayNome(a.USRNOMCPL ?? a.usrnomcpl ?? a.USUSEQ ?? a.ususeq, a)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group mb-2">
                            <label>Data início</label>
                            <input type="date" value={novaDataInicio} onChange={(e) => setNovaDataInicio(e.target.value)} className="form-control form-control-sm" />
                        </div>
                        <div className="form-group mb-2">
                            <label>Data fim</label>
                            <input type="date" value={novaDataFim} onChange={(e) => setNovaDataFim(e.target.value)} className="form-control form-control-sm" />
                        </div>
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModalNova(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={handleCriarSprint}>Criar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SprintHome;
